import { Dialog } from "primereact/dialog";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import PPDecimalInput from "../../common/PPDecimalInput";
import { useProductRule } from "../hooks/use-productRuleData";
import { useOptions } from "../hooks/use-optionsData";
import { useCategoryData } from "../hooks/use-categoryData";

const normalizeLanguageOption = (option) => {
     if (option === null || option === undefined || option === "") return null;
     const value = typeof option === "object"
          ? option.value ?? option.lang ?? option.label
          : option;
     if (value === null || value === undefined || value === "") return null;
     return {
          ...(typeof option === "object" ? option : {}),
          label: String(typeof option === "object" ? option.label ?? value : value),
          value
     };
};

const normalizeChannelOption = (option) => {
     if (option === null || option === undefined || option === "") return null;
     const value = typeof option === "object"
          ? option.value ?? option.channelName ?? option.label
          : option;
     if (value === null || value === undefined || value === "") return null;
     return {
          ...(typeof option === "object" ? option : {}),
          label: String(typeof option === "object" ? option.label ?? option.channelName ?? value : value),
          value
     };
};

function PPPriceManagementProductRuleModal({ visible, close, editingRule, onChannelContextChange }) {

     const [t] = useTranslation();
     const [selectedRule, setSelectedRule] = useState(null);
     const [saveAction, setSaveAction] = useState(null);
     const [canSave, setCanSave] = useState(false);

     const categories = [
          { label: "Produkt-Kategorie", id: 0, value: 0 },
          { label: "Ranking- & Preis-Filter", id: 1, value: 1 },
          { label: "Verfügbarkeit", id: 2, value: 2 },
          { label: "Produkt-Attribute", id: 3, value: 3 },
          { label: "Sprache & Kanal", id: 4, value: 4 },
     ]

     const getCategory = (keyString) => {
          const mapping = {
               'productCategory': 0,
               'rankingAndFilter': 1,
               'stock': 2,
               'product-attribute': 3,
               'languageAndChannel': 4
          };
          return mapping[keyString] ?? null;
     };
     const lockedCategoryId = editingRule ? getCategory(editingRule.keyString) : null;

     useEffect(() => {
          if (!visible) {
               return;
          }

          if (editingRule) {
               const categoryId = getCategory(editingRule.keyString);
               if (categoryId !== null) {
                    const category = categories.find(cat => cat.id === categoryId);
                    if (category) {
                         setSelectedRule(category);
                         return;
                    }
               }
          }

          setSelectedRule(categories[0]);
     }, [visible, editingRule]);

     function closeModal() {
          setSelectedRule(null);
          setCanSave(false);
          close();
     }

     const handleSave = () => {
          if (saveAction && canSave) {
               saveAction();
               closeModal();
          }
     };

     return (
          <Dialog
               header={editingRule ? `Produkt-Regel bearbeiten - ${editingRule.name || editingRule.keyString}` : "Regel - Eigene Produkte"}
               footer={
                    <>
                         <button className="btn btn-danger btn-lg m-2" onClick={closeModal}>
                              {t("single.cancel")}
                         </button>
                         {selectedRule && selectedRule.id !== undefined && (
                              <button
                                   className="btn btn-primary btn-lg m-2"
                                   onClick={handleSave}
                                   disabled={!canSave}
                              >
                                   <i className="fas fa-save me-2"></i>
                                   {editingRule ? 'Aktualisieren' : 'Speichern'}
                              </button>
                         )}
                    </>
               }
               visible={visible}
               style={{ width: '60vw', height: '70vh' }}
               onHide={closeModal}
          >
               <div className="mb-3">
                    <div className="d-flex flex-wrap gap-2">
                         {categories.map((cat) => {
                              const isActive = selectedRule && selectedRule.id === cat.id;
                              const btnClass = isActive ? "btn btn-primary" : "btn btn-outline-primary";
                              return (
                                   <button
                                        key={cat.id}
                                        type="button"
                                        className={`${btnClass} btn-sm`}
                                        onClick={() => setSelectedRule(cat)}
                                   disabled={lockedCategoryId !== null && cat.id !== lockedCategoryId}
                                   >
                                        {cat.label}
                                   </button>
                              );
                         })}
                    </div>
               </div>
               <PPPriceManagementProductRuleTile
                    selectedRule={selectedRule}
                    editingRule={editingRule}
                    close={closeModal}
                    setSaveAction={setSaveAction}
                    setCanSave={setCanSave}
                    onChannelContextChange={onChannelContextChange}
               />
          </Dialog>
     );
}
export default PPPriceManagementProductRuleModal;

function PPPriceManagementProductRuleTile({ selectedRule, editingRule, close, setSaveAction, setCanSave, onChannelContextChange }) {
     if (!selectedRule || selectedRule.id === undefined) {
          return null;
     }
     switch (selectedRule.id) {
          case 0:
               return <PPPriceManagementProductCategoryModal closeModal={close} editingRule={editingRule} setSaveAction={setSaveAction} setCanSave={setCanSave} />;
          case 1:
               return <PPPriceManagementRankingPriceFilterModal closeModal={close} editingRule={editingRule} setSaveAction={setSaveAction} setCanSave={setCanSave} />;
          case 2:
               return <PPPriceManagementStockModal closeModal={close} editingRule={editingRule} setSaveAction={setSaveAction} setCanSave={setCanSave} />;
          case 3:
               return <PPPriceManagementProductAttributeModal closeModal={close} editingRule={editingRule} setSaveAction={setSaveAction} setCanSave={setCanSave} />;
          case 4:
               return <PPPriceManagementLanguageAndChannelModal closeModal={close} editingRule={editingRule} setSaveAction={setSaveAction} setCanSave={setCanSave} onChannelContextChange={onChannelContextChange} />;
          default:
               return "";
     }
}

function PPPriceManagementLanguageAndChannelModal({ setSaveAction, setCanSave, editingRule, onChannelContextChange }) {
     const { LanguageAndChannelHelper } = useProductRule();
     const { getProductRuleOptions } = useOptions();

     const options = getProductRuleOptions().languageAndChannel;

     const [localState, setLocalState] = useState({
          language: null,
          channel: null
     });

     useEffect(() => {
          if (editingRule && editingRule.keyString === 'languageAndChannel') {
               setLocalState({
                    language: normalizeLanguageOption(editingRule.language),
                    channel: normalizeChannelOption(editingRule.channel)
               });
          } else {
               setLocalState({
                    language: normalizeLanguageOption(LanguageAndChannelHelper.getLanguage()),
                    channel: normalizeChannelOption(LanguageAndChannelHelper.getChannel())
               });
          }
     }, [editingRule, LanguageAndChannelHelper]);

     useEffect(() => {
          setSaveAction(() => () => {
               LanguageAndChannelHelper.setLanguage(localState.language);
               LanguageAndChannelHelper.setChannel(localState.channel);
               LanguageAndChannelHelper.toggleLanguageAndChannel(true);
               onChannelContextChange?.({
                    language: localState.language,
                    channel: localState.channel
               });
          });
          setCanSave(!!localState.language && !!localState.channel);
     }, [setSaveAction, setCanSave, localState, LanguageAndChannelHelper, onChannelContextChange]);

     return (
          <div className="pt-3">
               <div className="card">
                    <div className="card-header">
                         <h5 className="mb-0">Sprache & Kanal</h5>
                    </div>
                    <div className="card-body">
                         <div className="mb-3">
                              <label htmlFor="product_rule_lang_select" className="form-label fw-semibold">Sprache</label>
                              <Select
                                   name="Test"
                                   className="w-100"
                                   inputId="product_rule_lang_select"
                                   options={options.language.map(normalizeLanguageOption).filter(Boolean)}
                                   value={localState.language}
                                   onChange={(element) => setLocalState(prev => ({ ...prev, language: element }))}
                                   placeholder="Sprache wählen"
                              />
                         </div>
                         <div className="mb-3">
                              <label htmlFor="product_rule_channel_select" className="form-label fw-semibold">Kanal</label>
                              <Select
                                   className="w-100"
                                   inputId="product_rule_channel_select"
                                   options={options.channel.map(normalizeChannelOption).filter(Boolean)}
                                   value={localState.channel}
                                   onChange={(element) => setLocalState(prev => ({ ...prev, channel: element }))}
                                   placeholder="Kanal wählen"
                              />
                         </div>
                    </div>
               </div>
          </div>
     );
}

function PPPriceManagementStockModal({ editingRule, setSaveAction, setCanSave }) {
     const { StockHelper } = useProductRule();
     const { getProductRuleOptions } = useOptions();

     const [stockList, setStockList] = useState(() => {
          if (editingRule && editingRule.keyString === 'stock') {
               return editingRule.stock || [false, false, false];
          }
          return [false, false, false];
     });

     const options = getProductRuleOptions().stock;
     const isEditing = editingRule && editingRule.keyString === 'stock';

     useEffect(() => {
          const anySelected = Array.isArray(stockList) && stockList.some(Boolean);
          setCanSave(anySelected);

          setSaveAction(() => () => {
               if (isEditing) {
                    StockHelper.updateStockRule(editingRule.id, 'stock', stockList);
               } else {
                    StockHelper.addStockRule({
                         stock: stockList
                    });
               }
          });
     }, [setSaveAction, isEditing, editingRule?.id, stockList, StockHelper, setCanSave]);

     function changeStockList(index, state) {
          setStockList((prev) => {
               const newList = [...prev];
               newList[index] = state;
               return newList;
          });
     }

     return (
          <div className="pt-3">
               <div className="card">
                    <div className="card-header">
                         <h5 className="mb-0">
                              {isEditing ? 'Verfügbarkeit bearbeiten' : 'Verfügbarkeit'}
                         </h5>
                         {isEditing && (
                              <small className="text-muted">
                                   Regel-ID: {editingRule.id}
                              </small>
                         )}
                    </div>
                    <div className="card-body">
                         <div className="ps-3">
                              <div className="form-check mb-3">
                                   <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="stock_red"
                                        onChange={(event) => changeStockList(0, event.target.checked)}
                                        checked={stockList[0]}
                                        defaultChecked={false}
                                   />
                                   <label className="form-check-label" htmlFor="stock_red">
                                        {options.stock[0]}
                                   </label>
                              </div>
                              <div className="form-check mb-3">
                                   <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="stock_green"
                                        onChange={(event) => changeStockList(1, event.target.checked)}
                                        checked={stockList[1]}
                                        defaultChecked={false}
                                   />
                                   <label className="form-check-label" htmlFor="stock_green">
                                        {options.stock[1]}
                                   </label>
                              </div>
                              <div className="form-check mb-0">
                                   <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="stock_yellow"
                                        onChange={(event) => changeStockList(2, event.target.checked)}
                                        checked={stockList[2]}
                                        defaultChecked={false}
                                   />
                                   <label className="form-check-label" htmlFor="stock_yellow">
                                        {options.stock[2]}
                                   </label>
                              </div>
                         </div>
                    </div>
               </div>
          </div>
     );
}

function PPPriceManagementRankingPriceFilterModal({ setSaveAction, setCanSave, editingRule }) {
     const { RankingAndFilterHelper } = useProductRule();

     const [localState, setLocalState] = useState({
          rankingFirst: null,
          rankingSecond: null,
          priceFirst: null,
          priceSecond: null
     });

     useEffect(() => {
          const initialState = {
               rankingFirst: RankingAndFilterHelper.getRankingFirst(),
               rankingSecond: RankingAndFilterHelper.getRankingSecond(),
               priceFirst: RankingAndFilterHelper.getPriceFirst(),
               priceSecond: RankingAndFilterHelper.getPriceSecond()
          };
          setLocalState(initialState);

          const hasValue = (val) =>
               val !== null &&
               val !== undefined &&
               !(typeof val === "string" && val.trim() === "");

          const hasAnyValue =
               hasValue(initialState.rankingFirst) ||
               hasValue(initialState.rankingSecond) ||
               hasValue(initialState.priceFirst) ||
               hasValue(initialState.priceSecond);

          setCanSave(hasAnyValue);
     }, [editingRule, RankingAndFilterHelper, setCanSave]);

     useEffect(() => {
          setSaveAction(() => () => {
               RankingAndFilterHelper.setRankingFirst(localState.rankingFirst);
               RankingAndFilterHelper.setRankingSecond(localState.rankingSecond);
               RankingAndFilterHelper.setPriceFirst(localState.priceFirst);
               RankingAndFilterHelper.setPriceSecond(localState.priceSecond);
               RankingAndFilterHelper.toggleRankingAndFilter(true);
          });

          const hasValue = (val) =>
               val !== null &&
               val !== undefined &&
               !(typeof val === "string" && val.trim() === "");

          const hasAnyValue =
               hasValue(localState.rankingFirst) ||
               hasValue(localState.rankingSecond) ||
               hasValue(localState.priceFirst) ||
               hasValue(localState.priceSecond);

          setCanSave(hasAnyValue);
     }, [setSaveAction, localState, RankingAndFilterHelper, setCanSave]);

     return (
          <div className="pt-3">
               <div className="card">
                    <div className="card-header">
                         <h5 className="mb-0">Ranking- & Preis-Filter</h5>
                    </div>
                    <div className="card-body">
                         <div className="mb-4">
                              <div className="d-flex align-items-center gap-2">
                                   <span className="fw-semibold text-nowrap">Produkt-Ranking</span>
                                   <span>&gt;</span>
                                   <PPDecimalInput
                                        value={localState.rankingFirst}
                                        onValueChange={(v) => setLocalState(prev => ({ ...prev, rankingFirst: v }))}
                                        className="form-control"
                                        style={{ width: '120px' }}
                                        decimalsLimit={2}
                                        suffix={"€"}
                                        step={1}
                                        maxLength={6}
                                        placeholder="0,00"
                                   />
                                   <span className="text-nowrap">und &lt;</span>
                                   <PPDecimalInput
                                        value={localState.rankingSecond}
                                        onValueChange={(v) => setLocalState(prev => ({ ...prev, rankingSecond: v }))}
                                        className="form-control"
                                        style={{ width: '120px' }}
                                        decimalsLimit={2}
                                        suffix={"€"}
                                        step={1}
                                        maxLength={3}
                                        placeholder="0,00"
                                   />
                              </div>
                         </div>

                         <div className="mb-0">
                              <div className="d-flex align-items-center gap-2">
                                   <span className="fw-semibold text-nowrap">Produkt-Preis</span>
                                   <span>&gt;</span>
                                   <PPDecimalInput
                                        value={localState.priceFirst}
                                        onValueChange={(v) => setLocalState(prev => ({ ...prev, priceFirst: v }))}
                                        className="form-control"
                                        style={{ width: '120px' }}
                                        decimalsLimit={2}
                                        suffix={"€"}
                                        step={1}
                                        maxLength={6}
                                        placeholder="0,00"
                                   />
                                   <span className="text-nowrap">und &lt;</span>
                                   <PPDecimalInput
                                        value={localState.priceSecond}
                                        onValueChange={(v) => setLocalState(prev => ({ ...prev, priceSecond: v }))}
                                        className="form-control"
                                        style={{ width: '120px' }}
                                        decimalsLimit={2}
                                        suffix={"€"}
                                        step={1}
                                        maxLength={3}
                                        placeholder="0,00"
                                   />
                              </div>
                         </div>
                    </div>
               </div>
          </div>
     );
}

function PPPriceManagementProductCategoryModal({ setSaveAction, setCanSave }) {
     const { ProductCategoryHelper } = useProductRule();
     const {
          departmentOptions,
          areaOptions,
          categoryOptions,
          articleOptions,
          manufacturerOptions,
          loadingStates,
          loadAreas,
          loadCategories,
          loadArticles,
          loadManufacturers
     } = useCategoryData();

     const [localSelections, setLocalSelections] = useState({
          departments: [],
          areas: [],
          categories: []
     });

     const [productNrListValue, setProductNrListValue] = useState("");
     const [articleSelection, setArticleSelection] = useState([]);
     const [providerSelection, setProviderSelection] = useState([]);
     const [filterProductNr, setFilterProductNr] = useState(true);

     useEffect(() => {
          setSaveAction(() => () => {
               ProductCategoryHelper.setDepartment(localSelections.departments.length > 0 ? localSelections.departments : null);
               ProductCategoryHelper.setArea(localSelections.areas.length > 0 ? localSelections.areas : null);
               ProductCategoryHelper.setCategory(localSelections.categories.length > 0 ? localSelections.categories : null);
               ProductCategoryHelper.setArticle(articleSelection.length > 0 ? articleSelection : null);
               ProductCategoryHelper.setProvider(providerSelection.length > 0 ? providerSelection : null);
               ProductCategoryHelper.setProductNrList(productNrListValue);
               ProductCategoryHelper.setFilterProductNr(filterProductNr);
               ProductCategoryHelper.toggleProductCategory(true);
          });
     }, [
          setSaveAction,
          ProductCategoryHelper,
          localSelections,
          articleSelection,
          providerSelection,
          productNrListValue,
          filterProductNr
     ]);

     useEffect(() => {
          let cancelled = false;
          const asArray = (value) => Array.isArray(value) ? value : value ? [value] : [];
          const matches = (stored, option, keys) =>
               keys.some((key) => String(stored?.[key] ?? "") === String(option?.[key] ?? ""));
          const reconcile = (storedValues, options, keys) =>
               storedValues.map((stored) =>
                    options.find((option) => !option.isAllOption && matches(stored, option, keys)) || stored
               );

          async function initializeSelections() {
               const storedDepartments = asArray(ProductCategoryHelper.getDepartment());
               const storedAreas = asArray(ProductCategoryHelper.getArea());
               const storedCategories = asArray(ProductCategoryHelper.getCategory());
               const storedArticles = asArray(ProductCategoryHelper.getArticle());
               const storedProviders = asArray(ProductCategoryHelper.getProvider());

               const departments = reconcile(storedDepartments, departmentOptions, ["catShort", "value", "label"]);
               setLocalSelections({ departments, areas: [], categories: [] });
               setProductNrListValue(ProductCategoryHelper.getProductNrList() || "");
               setFilterProductNr(ProductCategoryHelper.getFilterProductNr() ?? true);

               const manufacturers = await loadManufacturers();
               if (cancelled) return;
               const providers = reconcile(storedProviders, manufacturers, ["name", "value", "label"]);
               setProviderSelection(providers);

               let areas = storedAreas;
               let categories = storedCategories;
               if (departments.length > 0) {
                    const loadedAreas = await loadAreas(departments);
                    if (cancelled) return;
                    areas = reconcile(storedAreas, loadedAreas, ["catShort", "value", "label"]);
                    setLocalSelections((previous) => ({ ...previous, areas }));
               }

               if (areas.length > 0) {
                    const loadedCategories = await loadCategories(areas);
                    if (cancelled) return;
                    categories = reconcile(storedCategories, loadedCategories, ["catShort", "value", "label"]);
                    setLocalSelections((previous) => ({ ...previous, categories }));
               }

               if (storedArticles.length > 0 && departments.length > 0) {
                    const loadedArticles = await loadArticles(departments, areas, categories, providers);
                    if (cancelled) return;
                    setArticleSelection(reconcile(storedArticles, loadedArticles, ["productnr", "value", "label"]));
               } else {
                    setArticleSelection(storedArticles);
               }
          }

          initializeSelections();
          return () => {
               cancelled = true;
          };
     }, []);

     useEffect(() => {
          const hasDepartments = localSelections.departments.length > 0;
          const hasAreas = localSelections.areas.length > 0;
          const hasCategories = localSelections.categories.length > 0;
          const hasArticles = Array.isArray(articleSelection) && articleSelection.length > 0;
          const hasProviders = Array.isArray(providerSelection) && providerSelection.length > 0;
          const hasProductNrList = typeof productNrListValue === "string" && productNrListValue.trim().length > 0;

          setCanSave(
               hasDepartments ||
               hasAreas ||
               hasCategories ||
               hasArticles ||
               hasProviders ||
               hasProductNrList
          );
     }, [localSelections, articleSelection, providerSelection, productNrListValue, setCanSave]);

     const handleDepartmentChange = (selectedOptions) => {
          let selectedDepartments = selectedOptions || [];

          const containsAllOptionsElement = selectedDepartments.find(sd => sd.isAllOption);
          if (containsAllOptionsElement) {
               selectedDepartments = containsAllOptionsElement.allEntries;
          }

          setLocalSelections(prev => ({
               ...prev,
               departments: selectedDepartments,
               areas: [],
               categories: []
          }));
          setArticleSelection([]);

          if (selectedDepartments.length > 0) {
               loadAreas(selectedDepartments);
          } else {
               loadAreas([]);
          }
     };

     const handleAreaChange = (selectedOptions) => {
          let selectedAreas = selectedOptions || [];

          const containsAllOptionsElement = selectedOptions.find(so => so.isAllOption);
          if (containsAllOptionsElement) {
               selectedAreas = containsAllOptionsElement.allEntries;
          }

          setLocalSelections(prev => ({
               ...prev,
               areas: selectedAreas,
               categories: []
          }));
          setArticleSelection([]);

          if (selectedAreas.length > 0) {
               loadCategories(selectedAreas);
          } else {
               loadCategories([]);
          }
     };

     const handleCategoryChange = (selectedOptions) => {
          let selectedCategories = selectedOptions || [];

          const containsAllOptionsElement = selectedCategories.find(sc => sc.isAllOption);
          if (containsAllOptionsElement) {
               selectedCategories = containsAllOptionsElement.allEntries;
          }

          setLocalSelections(prev => ({
               ...prev,
               categories: selectedCategories
          }));
          setArticleSelection([]);
     };

     const handleArticleChange = (selectedOptions) => {
          let selectedArticles = selectedOptions || [];

          const containsAllOptionsElement = selectedArticles.find(sa => sa.isAllOption);
          if (containsAllOptionsElement) {
               selectedArticles = containsAllOptionsElement.allEntries;
          }
          setArticleSelection(selectedArticles);
     };

     const handleProducerChange = (selectedOptions) => {
          let selectedProducer = selectedOptions || [];

          const containsAllOptionsElement = selectedProducer.find(sp => sp.isAllOption);
          if (containsAllOptionsElement) {
               selectedProducer = containsAllOptionsElement.allEntries;
          }
          setProviderSelection(selectedProducer);
          setArticleSelection([]);
     };

     const handleArticleMenuOpen = () => {
          if (localSelections.departments.length === 0) return;
          loadArticles(localSelections.departments, localSelections.areas, localSelections.categories, providerSelection);
     };

     const getCurrentDepartments = () => {
          return localSelections.departments;
     };

     const getCurrentAreas = () => {
          return localSelections.areas;
     };

     const getCurrentCategories = () => {
          return localSelections.categories;
     };

     return (
          <div className="pt-3">
               <div className="card">
                    <div className="card-header">
                         <h5 className="mb-0">Produkt-Kategorie</h5>
                    </div>
                    <div className="card-body">
                         <div className="row">
                              <div className="col-md-6">
                                   <div className="mb-3">
                                        <label htmlFor="product_cat_department_select" className="form-label fw-semibold">Abteilung</label>
                                        <Select
                                             name="Test"
                                             className="w-100"
                                             inputId="product_cat_department_select"
                                             options={departmentOptions}
                                             value={getCurrentDepartments()}
                                             onChange={handleDepartmentChange}
                                             placeholder="Abteilung(en) wählen"
                                             isMulti
                                             isClearable
                                             styles={{
                                                  valueContainer: (provided) => ({
                                                       ...provided,
                                                       maxHeight: '100px',
                                                       overflowY: 'auto'
                                                  })
                                             }}
                                        />
                                   </div>

                                   <div className="mb-3">
                                        <label htmlFor="product_cat_area_select" className="form-label fw-semibold">Bereich</label>
                                        <Select
                                             name="Test"
                                             className="w-100"
                                             inputId="product_cat_area_select"
                                             options={areaOptions}
                                             value={getCurrentAreas()}
                                             onChange={handleAreaChange}
                                             placeholder="Bereich(e) wählen"
                                             isMulti
                                             isClearable
                                             isDisabled={localSelections.departments.length === 0}
                                             isLoading={loadingStates.areas}
                                             styles={{
                                                  valueContainer: (provided) => ({
                                                       ...provided,
                                                       maxHeight: '100px',
                                                       overflowY: 'auto'
                                                  })
                                             }}
                                        />
                                   </div>

                                   <div className="mb-3">
                                        <label htmlFor="product_cat_cat_select" className="form-label fw-semibold">Kategorie</label>
                                        <Select
                                             name="Test"
                                             className="w-100"
                                             inputId="product_cat_cat_select"
                                             options={categoryOptions}
                                             value={getCurrentCategories()}
                                             onChange={handleCategoryChange}
                                             placeholder="Kategorie(n) wählen"
                                             isMulti
                                             isClearable
                                             isDisabled={localSelections.areas.length === 0}
                                             isLoading={loadingStates.categories}
                                             styles={{
                                                  valueContainer: (provided) => ({
                                                       ...provided,
                                                       maxHeight: '100px',
                                                       overflowY: 'auto'
                                                  })
                                             }}
                                        />
                                   </div>
                              </div>

                              <div className="col-md-6">
                                   <div className="mb-3">
                                        <label htmlFor="product_cat_article_select" className="form-label fw-semibold">Artikel</label>
                                        <Select
                                             name="Test"
                                             className="w-100"
                                             inputId="product_cat_article_select"
                                             options={articleOptions}
                                             value={articleSelection}
                                             isMulti
                                             onChange={handleArticleChange}
                                             placeholder="Artikel wählen"
                                             isClearable
                                             isDisabled={localSelections.departments.length === 0}
                                             isLoading={loadingStates.articles}
                                             onMenuOpen={handleArticleMenuOpen}
                                             styles={{
                                                  valueContainer: (provided) => ({
                                                       ...provided,
                                                       maxHeight: '100px',
                                                       overflowY: 'auto'
                                                  })
                                             }}
                                        />
                                   </div>

                                   <div className="mb-3">
                                        <label htmlFor="product_cat_provider_select" className="form-label fw-semibold">Hersteller</label>
                                        <Select
                                             name="Test"
                                             className="w-100"
                                             inputId="product_cat_provider_select"
                                             options={manufacturerOptions}
                                             value={providerSelection}
                                             isMulti
                                             onChange={handleProducerChange}
                                             placeholder="Hersteller wählen"
                                             isClearable
                                             isDisabled={false}
                                             isLoading={loadingStates.manufacturers}
                                             styles={{
                                                  valueContainer: (provided) => ({
                                                       ...provided,
                                                       maxHeight: '100px',
                                                       overflowY: 'auto'
                                                  })
                                             }}
                                        />
                                   </div>
                              </div>
                         </div>

                         <div className="border-top pt-3 mt-3">
                              <div className="mb-3">
                                   <label htmlFor="inputmax_product_number" className="form-label fw-semibold">Produktnummer Liste (kommasepariert)</label>
                                   <input
                                        value={productNrListValue}
                                        onChange={(e) => {
                                             setProductNrListValue(e.target.value);
                                        }}
                                        className="form-control"
                                        type="text"
                                        id="inputmax_product_number"
                                        placeholder="123, 456, 789"
                                   />
                              </div>

                              <div className="ps-3">
                                   <div className="form-check form-switch mb-2">
                                        <input
                                             className="form-check-input"
                                             type="checkbox"
                                             role="switch"
                                             id="filterProductNr"
                                             checked={filterProductNr}
                                             onChange={() => setFilterProductNr(true)}
                                        />
                                        <label className="form-check-label" htmlFor="filterProductNr">Nach Produktnummern filtern</label>
                                   </div>

                                   <div className="form-check form-switch">
                                        <input
                                             className="form-check-input"
                                             type="checkbox"
                                             role="switch"
                                             id="excludeProductNr"
                                             checked={!filterProductNr}
                                             onChange={() => setFilterProductNr(false)}
                                        />
                                        <label className="form-check-label" htmlFor="excludeProductNr">Produktnummern ausschließen</label>
                                   </div>
                              </div>
                         </div>
                    </div>
               </div>
          </div>
     );
}

function PPPriceManagementProductAttributeModal({ setSaveAction, setCanSave }) {
     const { ProductAttributeHelper } = useProductRule();
     const { getProductAttributeOptions, fetchProductAttributeStringValues } = useOptions();

     const options = getProductAttributeOptions();

     const [localState, setLocalState] = useState({
          selectedAttribute: null,
          selectedOperator: null,
          comparisonValue: ""
     });

     const stringOperatorOptions = (options.stringOperators || ["=", "IN", "CONTAINS"]).map((operator) => ({
          value: operator,
          label: operator
     }));

     const [equation, setEquation] = useState([]);
     const [stringValueOptions, setStringValueOptions] = useState([]);
     const [loadingStringValues, setLoadingStringValues] = useState(false);

     useEffect(() => {
          const existingEquation = ProductAttributeHelper.getEquation() || [];
          setEquation(existingEquation);
          setCanSave(Array.isArray(existingEquation) && existingEquation.length > 0);
     }, [ProductAttributeHelper, setCanSave]);

     useEffect(() => {
          setSaveAction(() => () => {
               ProductAttributeHelper.setEquation(equation);
               ProductAttributeHelper.toggleProductAttribute(true);
          });
          setCanSave(Array.isArray(equation) && equation.length > 0);
     }, [setSaveAction, ProductAttributeHelper, equation, setCanSave]);

     useEffect(() => {
          let cancelled = false;

          async function loadStringValues() {
               const attribute = localState.selectedAttribute;
               const attributeType = getAttributeType(attribute);
               if (attributeType !== "string" && attributeType !== "tag") {
                    setStringValueOptions([]);
                    setLoadingStringValues(false);
                    return;
               }

               setLoadingStringValues(true);
               try {
                    const values = await fetchProductAttributeStringValues(attribute.value);
                    if (!cancelled) {
                         setStringValueOptions(values || []);
                    }
               } catch (error) {
                    if (!cancelled) {
                         setStringValueOptions([]);
                    }
               } finally {
                    if (!cancelled) {
                         setLoadingStringValues(false);
                    }
               }
          }

          loadStringValues();
          return () => {
               cancelled = true;
          };
     }, [localState.selectedAttribute, fetchProductAttributeStringValues]);

     function getAttributeType(attribute) {
          const type = String(attribute?.type || "numeric").toLowerCase();
          if (type === "string" || type === "boolean" || type === "tag") {
               return type;
          }
          return "numeric";
     }

     function isTextualAttributeType(attributeType) {
          return attributeType === "string" || attributeType === "tag";
     }

     const attributeType = getAttributeType(localState.selectedAttribute);
     const isNumericAttribute = attributeType === "numeric";
     const isBooleanAttribute = attributeType === "boolean";
     const isTextualAttribute = isTextualAttributeType(attributeType);
     const selectedStringOperator = localState.selectedOperator || "=";
     const usesFreeTextValue = isTextualAttribute && (
          selectedStringOperator === "IN" ||
          selectedStringOperator === "CONTAINS" ||
          (attributeType === "tag" && selectedStringOperator === "=")
     );
     const valueOptions = isBooleanAttribute
          ? [{ value: "True", label: "True" }, { value: "False", label: "False" }]
          : stringValueOptions;

     const addAttributeTerm = () => {
          const hasComparisonValue =
               localState.comparisonValue !== null &&
               localState.comparisonValue !== undefined &&
               String(localState.comparisonValue).trim() !== "";

          if (!localState.selectedAttribute || !localState.selectedOperator || !hasComparisonValue) {
               return;
          }

          const attribute = localState.selectedAttribute.value || localState.selectedAttribute.label;
          const comparisonValue = String(localState.comparisonValue).trim().replace(/'/g, "\\'");
          let term;

          if (isNumericAttribute) {
               term = `${attribute}${localState.selectedOperator}${comparisonValue}`;
          } else if (isBooleanAttribute) {
               term = `${attribute}='${comparisonValue}'`;
          } else if (selectedStringOperator === "IN") {
               term = `${attribute} IN ${comparisonValue}`;
          } else if (selectedStringOperator === "CONTAINS") {
               term = `${attribute} CONTAINS ${comparisonValue}`;
          } else if (attributeType === "tag") {
               term = `${attribute}=${comparisonValue}`;
          } else {
               term = `${attribute}='${comparisonValue}'`;
          }

          setEquation([...equation, term]);
          setLocalState({
               selectedAttribute: null,
               selectedOperator: null,
               comparisonValue: ""
          });
     };

     const addOperator = (operator) => {
          const newEquation = [...equation, operator];
          setEquation(newEquation);
     };

     const addBracket = (bracket) => {
          const newEquation = [...equation, bracket];
          setEquation(newEquation);
     };

     const removeLastTerm = () => {
          if (equation.length > 0) {
               const newEquation = equation.slice(0, -1);
               setEquation(newEquation);
          }
     };

     const clearAll = () => {
          setEquation([]);
     };

     return (
          <div className="pt-3">
               <div className="card">
                    <div className="card-header">
                         <h5 className="mb-0">Produkt-Attribute</h5>
                    </div>
                    <div className="card-body">
                         <div className="mb-4">
                              <h6 className="fw-semibold mb-3">Vergleich erstellen</h6>
                              <div className="row align-items-end">
                                   <div className="col-md-4">
                                        <label htmlFor="product-attr-attribute" className="form-label">Attribut</label>
                                        <Select
                                             inputId="product-attr-attribute"
                                             className="w-100"
                                             options={options.attributeKeys}
                                             value={localState.selectedAttribute}
                                             onChange={(element) => {
                                                  const nextType = getAttributeType(element);
                                                  setLocalState({
                                                       selectedAttribute: element,
                                                       selectedOperator: nextType === "numeric" ? null : "=",
                                                       comparisonValue: ""
                                                  });
                                             }}
                                             placeholder="Attribut wählen"
                                             isClearable
                                        />
                                   </div>
                                   {isNumericAttribute && (
                                        <div className="col-md-3">
                                             <label htmlFor="product-attr-operator-numeric" className="form-label">Operator</label>
                                             <Select
                                                  inputId="product-attr-operator-numeric"
                                                  className="w-100"
                                                  options={options.operators.map(op => ({ value: op, label: op }))}
                                                  value={localState.selectedOperator ? { value: localState.selectedOperator, label: localState.selectedOperator } : null}
                                                  onChange={(element) => setLocalState(prev => ({ ...prev, selectedOperator: element?.value || null }))}
                                                  placeholder="Operator wählen"
                                                  isClearable
                                             />
                                        </div>
                                   )}
                                   {isTextualAttribute && (
                                        <div className="col-md-3">
                                             <label htmlFor="product-attr-operator-textual" className="form-label">Operator</label>
                                             <Select
                                                  inputId="product-attr-operator-textual"
                                                  className="w-100"
                                                  options={stringOperatorOptions}
                                                  value={localState.selectedOperator
                                                       ? { value: localState.selectedOperator, label: localState.selectedOperator }
                                                       : { value: "=", label: "=" }}
                                                  onChange={(element) => setLocalState(prev => ({
                                                       ...prev,
                                                       selectedOperator: element?.value || "=",
                                                       comparisonValue: ""
                                                  }))}
                                                  placeholder="Operator wählen"
                                             />
                                        </div>
                                   )}
                                   <div className={isNumericAttribute || isTextualAttribute ? "col-md-3" : "col-md-6"}>
                                        <label htmlFor="product-attr-value" className="form-label">Wert</label>
                                        {isNumericAttribute ? (
                                             <PPDecimalInput
                                                  id="product-attr-value"
                                                  className="form-control"
                                                  value={localState.comparisonValue}
                                                  onValueChange={(v) => setLocalState(prev => ({ ...prev, comparisonValue: v ?? "" }))}
                                                  decimalsLimit={4}
                                                  disableGroupSeparators
                                                  allowNegativeValue
                                                  placeholder="Wert eingeben"
                                             />
                                        ) : usesFreeTextValue ? (
                                             <input
                                                  type="text"
                                                  id="product-attr-value"
                                                  className="form-control"
                                                  value={localState.comparisonValue}
                                                  onChange={(e) => setLocalState(prev => ({ ...prev, comparisonValue: e.target.value }))}
                                                  placeholder={
                                                       selectedStringOperator === "IN"
                                                            ? "tag1, tag2, tag3"
                                                            : attributeType === "tag"
                                                                 ? "Tag eingeben"
                                                                 : "Suchbegriff"
                                                  }
                                             />
                                        ) : (
                                             <Select
                                                  inputId="product-attr-value"
                                                  className="w-100"
                                                  options={valueOptions}
                                                  value={localState.comparisonValue
                                                       ? { value: localState.comparisonValue, label: localState.comparisonValue }
                                                       : null}
                                                  onChange={(element) => setLocalState(prev => ({
                                                       ...prev,
                                                       comparisonValue: element?.value || ""
                                                  }))}
                                                  placeholder={loadingStringValues ? "Lade Werte..." : "Wert wählen"}
                                                  isClearable
                                                  isLoading={loadingStringValues}
                                                  isDisabled={loadingStringValues}
                                             />
                                        )}
                                   </div>
                                   <div className="col-md-2">
                                        <button
                                             type="button"
                                             className="btn btn-primary w-100"
                                             onClick={addAttributeTerm}
                                             disabled={
                                                  !localState.selectedAttribute ||
                                                  !localState.selectedOperator ||
                                                  localState.comparisonValue === null ||
                                                  localState.comparisonValue === undefined ||
                                                  String(localState.comparisonValue).trim() === ""
                                             }
                                        >
                                             Hinzufügen
                                        </button>
                                   </div>
                              </div>
                         </div>

                         <div className="mb-4">
                              <h6 className="fw-semibold mb-3">Formel-Steuerung</h6>
                              <div className="d-flex flex-wrap gap-2">
                                   <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => addBracket("(")}
                                   >
                                        (
                                   </button>
                                   <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => addBracket(")")}
                                   >
                                        )
                                   </button>
                                   <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => addOperator("AND")}
                                   >
                                        AND
                                   </button>
                                   <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => addOperator("OR")}
                                   >
                                        OR
                                   </button>
                                   <button
                                        type="button"
                                        className="btn btn-outline-danger"
                                        onClick={removeLastTerm}
                                        disabled={equation.length === 0}
                                   >
                                        Letzten löschen
                                   </button>
                                   <button
                                        type="button"
                                        className="btn btn-outline-danger"
                                        onClick={clearAll}
                                        disabled={equation.length === 0}
                                   >
                                        Alle löschen
                                   </button>
                              </div>
                         </div>

                         <div className="mb-0">
                              <h6 className="fw-semibold mb-3">Aktuelle Formel</h6>
                              <div className="border p-3 bg-light rounded">
                                   {equation.length > 0 ? (
                                        <div className="d-flex flex-wrap gap-1">
                                             {equation.map((term, index) => (
                                                  <span key={index} className="badge bg-primary">
                                                       {term}
                                                  </span>
                                             ))}
                                        </div>
                                   ) : (
                                        <span className="text-muted fst-italic">Keine Formel definiert</span>
                                   )}
                              </div>
                              <small className="text-muted">
                                   Die Formel wird als: <code>{equation.join(" ")}</code> gespeichert
                              </small>
                         </div>
                    </div>
               </div>
          </div>
     );
}